import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import IFramePanel from '../../../components/IframePanel';
import { useSuggestions } from '../../../context/SuggestionsContext';

/**
 * Global feedback surfaces for notification actions (execute-proc loading,
 * success/error dialogs, and the in-app iframe action panel). These react to
 * SuggestionsContext state, so both embedded notification views mount one.
 * Mirrors the home page's handling, minus the Copilot support-chat button (the
 * widget isn't bootstrapped in embedded mode) — the support message is shown
 * inline instead.
 */
export const NotificationFeedback: React.FC = () => {
  const {
    suggestionSubmitting,
    suggestionError,
    supportMessage,
    suggestionFinishedSubmitting,
    refreshSuggestions,
    isPanelOpen,
    panelUrl,
    panelSuggestionId,
    closePanel,
    dismissSuggestionById,
  } = useSuggestions();

  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [prevSubmitting, setPrevSubmitting] = useState(false);

  useEffect(() => {
    if (suggestionError) setShowError(true);
  }, [suggestionError]);

  useEffect(() => {
    if (prevSubmitting && !suggestionSubmitting && !suggestionError) setShowSuccess(true);
    setPrevSubmitting(suggestionSubmitting);
  }, [suggestionSubmitting, suggestionError, prevSubmitting]);

  const closeSuccess = () => { setShowSuccess(false); suggestionFinishedSubmitting(false, null, null); };
  const closeError = () => { setShowError(false); suggestionFinishedSubmitting(false, null, null); };

  const handlePanelDismiss = async () => {
    closePanel();
    if (panelSuggestionId) {
      try {
        await dismissSuggestionById(panelSuggestionId, 'Action Performed');
        refreshSuggestions();
      } catch (error) {
        console.error('Error dismissing suggestion after panel action:', error);
      }
    }
  };

  return (
    <>
      {/* Loading */}
      <Dialog open={suggestionSubmitting}>
        <DialogContent className="max-w-xs" onInteractOutside={(e) => e.preventDefault()}>
          <div className="flex flex-col items-center gap-4 py-4 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
            <span className="text-sm text-muted-foreground">Processing...</span>
          </div>
        </DialogContent>
      </Dialog>

      {/* Error */}
      <Dialog open={showError} onOpenChange={(open) => !open && closeError()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Error</DialogTitle>
          </DialogHeader>
          <Alert variant="destructive">
            <AlertDescription>
              {suggestionError || 'An error occurred while processing your request.'}
            </AlertDescription>
          </Alert>
          {supportMessage && (
            <p className="text-xs text-muted-foreground whitespace-pre-wrap">{supportMessage}</p>
          )}
          <DialogFooter>
            <Button variant="destructive" onClick={closeError}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Success */}
      <Dialog open={showSuccess} onOpenChange={(open) => !open && closeSuccess()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Success</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">Action completed successfully!</p>
          <DialogFooter>
            <Button onClick={closeSuccess}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <IFramePanel
        isOpen={isPanelOpen}
        onDismiss={handlePanelDismiss}
        title="Action"
        url={panelUrl}
        type="large"
        isBlocking={false}
        focusTrapZoneProps={{}}
      />
    </>
  );
};
